// s16 Профиль — Mini-sprint 6-profile.
// Source: docs/specs/sprint-6-profile/stage-4-frontend.md §3.
//
// Read-only User card + ссылки на «Историю загрузок» и «Анкету здоровья»
// + кнопка «Выйти» + футер с версией. Никаких редактирований / push-toggle /
// «Помощь и поддержка» / «Правила и privacy» / удаления аккаунта (см. issues.md
// D-S6P-1..4 — все четыре блока отброшены).
//
// /profile обёрнут в RequireSurveyCompleted (App.tsx:265-271) — пользователь
// сюда попадает только с completed survey. formatSurveyStatus покрывает
// остальные ветки как defensive helper для регрессии routing'а.

import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Clock, FileText, HelpCircle, LogOut } from 'lucide-react';
import { Link, useLocation } from 'wouter';

import { useLogoutMutation } from '@/api/hooks/auth';
import { useProfileMe } from '@/api/hooks/profile';
import { useUploadHistory } from '@/api/hooks/uploads';
import { queryKeys } from '@/api/queryKeys';
import { NotificationsRow } from '@/components/push/NotificationsRow';
import { BottomNav } from '@/components/layout/BottomNav';
import { IconTile } from '@/components/ui/icon-tile';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Button } from '@/components/ui/button';
import { WellnessInsightsCard } from '@/components/wellness/WellnessInsightsCard';
import { APP_VERSION_LINE } from '@/lib/app-version';
import { clearToken } from '@/lib/auth-storage';
import { clearMarkerNavState } from '@/lib/markerNavState';
import { formatSurveyStatus, formatUploadsCounter, initialFromName } from '@/lib/profile-format';
import { setUploadsOrigin, UPLOADS_SECTION } from '@/lib/uploadNav';
import { setGuideOrigin } from '@/lib/guideNav';

export function ProfileTab() {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const profileQuery = useProfileMe();
  const uploadsAllQuery = useUploadHistory();
  const uploadsSuccessQuery = useUploadHistory({ status: 'success' });
  const logoutMut = useLogoutMutation();

  const user = profileQuery.data?.user;
  const survey = profileQuery.data?.survey;
  const total = uploadsAllQuery.data?.meta.total;
  const recognized = uploadsSuccessQuery.data?.meta.total;

  const initial = initialFromName(user?.name);
  const uploadsLoading =
    uploadsAllQuery.isLoading || uploadsSuccessQuery.isLoading;
  const uploadsLine =
    uploadsLoading || total === undefined || recognized === undefined
      ? '—'
      : formatUploadsCounter(total, recognized);
  const surveyLine = survey ? formatSurveyStatus(survey) : '—';

  async function handleLogout() {
    try {
      await logoutMut.mutateAsync();
    } catch {
      // Backend 401/500/network — продолжаем local logout, иначе пользователь
      // зависнет в "вечном pending". Issues §D-S6P-8 + stage-5 §3.3.
    }
    qc.clear();
    clearMarkerNavState(); // HEAL-9: не оставлять search/filter/origin для след. юзера
    await clearToken();
    setLocation('/signin');
  }

  return (
    <MobileFrame>
      <ScreenHeader title="Профиль" />
      <main className="flex flex-1 flex-col overflow-y-auto px-5 pb-5 pt-2">
        {/* User card — read-only */}
        <div className="mb-5 flex items-center gap-3 rounded-surface border border-border bg-surface p-4">
          <div
            aria-hidden
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xl font-bold text-primary-text"
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold leading-tight">
              {user?.name ?? 'Без имени'}
            </div>
            <div className="truncate text-hl-small text-muted-foreground">
              {user?.email ?? ''}
            </div>
          </div>
        </div>

        {/* Данные */}
        <div className="mb-2 text-hl-small font-bold uppercase tracking-wider text-muted-foreground">
          Данные
        </div>
        <div className="mb-5 divide-y divide-border rounded-surface border border-border bg-surface">
          <button
            type="button"
            onClick={() => {
              // Запоминаем раздел: «Назад» из «Моих загрузок» вернёт сюда, а не
              // на Главную (HEAL-82).
              setUploadsOrigin('profile');
              setLocation(UPLOADS_SECTION);
            }}
            className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted"
          >
            <IconTile size="sm" aria-hidden>
              <Clock className="h-5 w-5" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">История загрузок</div>
              <div className="text-hl-small text-muted-foreground">{uploadsLine}</div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setLocation('/survey/step-1')}
            className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted"
          >
            <IconTile size="sm" aria-hidden>
              <FileText className="h-5 w-5" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Анкета здоровья</div>
              <div className="text-hl-small text-muted-foreground">{surveyLine}</div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" aria-hidden />
          </button>
          {/* HEAL-46b D-32 — дорога назад для того, кто отказался от уведомлений
              при загрузке. На iOS системное окно показывается один раз, и без
              этой строки вернуться человеку некуда. На вебе строка не
              отображается вовсе: push в браузере не делаем. */}
          <NotificationsRow
            pushEnabled={user?.push_enabled ?? true}
            onChanged={() => void qc.invalidateQueries({ queryKey: queryKeys.profileMe })}
          />
        </div>

        {/* Обучение — предложение Алины из HEAL-63, принято без изменений.
            Второй вход в гайд: первый — строка на пустой Главной. */}
        <div className="mb-2 text-hl-small font-bold uppercase tracking-wider text-muted-foreground">
          Обучение
        </div>
        <div className="mb-5 rounded-surface border border-border bg-surface">
          <button
            type="button"
            onClick={() => {
              setGuideOrigin('/profile');
              setLocation('/guide');
            }}
            className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted"
          >
            <IconTile size="sm" aria-hidden>
              <HelpCircle className="h-5 w-5" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Как это работает</div>
              <div className="text-hl-small text-muted-foreground">Короткий гайд по приложению</div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" aria-hidden />
          </button>
        </div>

        {/* Подсказки по анкете (перенесены с Главной, Sprint 6-home-redesign:
            survey-based контент логичен рядом с анкетой) */}
        <div className="mb-2 text-hl-small font-bold uppercase tracking-wider text-muted-foreground">
          Рекомендации
        </div>
        <div className="mb-5">
          <WellnessInsightsCard />
        </div>

        {/* Logout */}
        {/* D-12: была красной, как «Удалить аккаунт». Выход обратим одним
            логином, удаление — нет, и одинаковый визуальный вес вводил в
            заблуждение. Красный остался только у необратимого. */}
        <Button
          type="button"
          variant="outline"
          onClick={handleLogout}
          disabled={logoutMut.isPending}
          className="w-full"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Выйти
        </Button>

        {/* HEAL-43: удаление аккаунта. Намеренно НЕ кнопка рядом с «Выйти» — эти два
            действия слишком разные по последствиям, чтобы стоять одинаково. Ссылка
            ведёт на отдельный экран с перечнем удаляемого и вводом пароля. */}
        <button
          type="button"
          onClick={() => setLocation('/profile/delete')}
          className="mt-4 self-center text-hl-secondary font-medium text-red-600 underline-offset-2 hover:underline"
        >
          Удалить аккаунт
        </button>

        {/* HEAL-3: документы должны открываться и из профиля. */}
        <div className="mt-5 text-center text-hl-small leading-snug text-muted-foreground">
          <Link href="/legal/privacy" className="underline-offset-2 hover:underline">
            Политика конфиденциальности
          </Link>
          {' · '}
          <Link href="/legal/tos" className="underline-offset-2 hover:underline">
            Условия использования
          </Link>
        </div>

        <div className="mt-3 text-center text-hl-small text-gray-400">
          {APP_VERSION_LINE}
        </div>
      </main>
      <BottomNav active="profile" />
    </MobileFrame>
  );
}
